import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col, Card, ListGroup, Badge, Button, OverlayTrigger, Tooltip, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Car, Clock, MapPin, CheckCircle, AlertCircle, AlertTriangle, XCircle, Info, ChevronRight, TrendingUp, Search, Calendar, Plane } from 'lucide-react';
import { format, startOfMonth, isSameDay, addDays, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { updateTrip, getTrips } from '../api';
import { toast } from 'react-toastify';

const SmartAssignmentView = ({ trips, drivers, vehicles, vehicleTypes, onRefresh }) => {
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [driverSearchTerm, setDriverSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('upcoming'); // 'today', 'tomorrow', 'upcoming', 'all'
    const [allMonthTrips, setAllMonthTrips] = useState([]);
    const [isMonthLoading, setIsMonthLoading] = useState(false);

    // Cargar historial de viajes del mes para cálculo real de ganancias y encadenamientos
    const loadMonthTrips = async () => {
        setIsMonthLoading(true);
        try {
            const now = new Date();
            const start = startOfMonth(now).toISOString();
            const end = addDays(now, 10).toISOString(); // cargar 10 días al futuro
            const data = await getTrips({
                start_date: start,
                end_date: end,
                limit: 2000
            });
            setAllMonthTrips(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error al cargar viajes del mes:", err);
        } finally {
            setIsMonthLoading(false);
        }
    };

    useEffect(() => {
        loadMonthTrips();
    }, [selectedTripId]); // recargar también si se asigna / cambia de viaje

    // Función auxiliar para detectar Aeropuerto
    const isAirport = (address) => {
        if (!address || typeof address !== 'string') return false;
        const lower = address.toLowerCase();
        if (lower.includes('aeropuerto') || lower.includes('airport') || lower.includes('carriel sur') || lower.includes('el tepual') || lower.includes('chacalluta') || lower.includes('el plumerillo') || lower.includes('ezeiza') || lower.includes('pudahuel')) {
            return true;
        }
        const iataCodes = ['scl', 'anf', 'cjc', 'iqq', 'lsc', 'ccp', 'pmc', 'zco', 'ari', 'mdz', 'eze', 'aep', 'puj', 'sdq', 'lim', 'bog', 'cpo', 'araucania'];
        const iataRegex = new RegExp(`\\b(${iataCodes.join('|')})\\b`, 'i');
        return iataRegex.test(address);
    };

    // 1. Filtrar viajes pendientes de asignación con filtro de fecha
    const pendingTrips = useMemo(() => {
        const now = new Date();
        const todayStart = startOfDay(now);
        const tomorrowStart = startOfDay(addDays(now, 1));
        const weekEnd = endOfDay(addDays(now, 7));

        return trips.filter(t => {
            const isPending = !t.driver_id && ['pending'].includes(t.status?.toLowerCase());
            if (!isPending) return false;

            const tripDate = new Date(t.scheduled_time);

            if (dateFilter === 'today') return isSameDay(tripDate, now);
            if (dateFilter === 'tomorrow') return isSameDay(tripDate, tomorrowStart);
            if (dateFilter === 'upcoming') return isWithinInterval(tripDate, { start: todayStart, end: weekEnd });
            
            return true; // 'all'
        }).sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
    }, [trips, dateFilter]);

    const selectedTrip = useMemo(() => 
        pendingTrips.find(t => t.id === selectedTripId), 
    [pendingTrips, selectedTripId]);

    // 2. LÓGICA DE RANKING INTELIGENTE
    const driverRanking = useMemo(() => {
        if (!selectedTrip) return [];

        const tripTime = new Date(selectedTrip.scheduled_time).getTime();
        const requestedTypeId = selectedTrip.requested_vehicle_type_id;
        const monthStart = startOfMonth(new Date());
        const today = new Date();

        return drivers.map(d => {
            if (!d.has_active_session) return null;
            let score = 0;
            const reasons = [];
            const vehicle = vehicles.find(v => v.current_driver_id === d.id && v.is_active);
            
            if (!vehicle) return null;

            // VETO 1: Capacidad de Pasajeros y Tipo de Vehículo
            const reqType = requestedTypeId ? vehicleTypes.find(vt => vt.id === requestedTypeId) : selectedTrip.requested_vehicle_type;
            const reqPax = Math.max(
                reqType?.capacity || 1,
                selectedTrip.passenger_count || 1
            );
            const currentType = vehicleTypes.find(vt => vt.id === vehicle.vehicle_type_id);
            const vehicleCapacity = currentType?.capacity || vehicle.vehicle_type?.capacity || 4;
            if (vehicleCapacity < reqPax) {
                return null; // VETO FÍSICO INFRANQUEABLE
            }
            if (reqType && vehicleCapacity < reqType.capacity) {
                return null;
            }

            // VETO 2: Base Autorizada
            if (selectedTrip.origin_base_id) {
                const isBaseAllowed = vehicle.permitted_bases?.some(b => String(b.id) === String(selectedTrip.origin_base_id));
                if (!isBaseAllowed) return null;
            }

            // FILTRO DE BÚSQUEDA MANUAL
            if (driverSearchTerm) {
                const lowerSearch = driverSearchTerm.toLowerCase();
                const matchesName = d.full_name.toLowerCase().includes(lowerSearch);
                const matchesVehicle = vehicle.internal_code?.toLowerCase().includes(lowerSearch) || 
                                     vehicle.plate?.toLowerCase().includes(lowerSearch);
                if (!matchesName && !matchesVehicle) return null;
            }

            // REGLA 1: Disponibilidad y Contador Diario (usando todos los viajes cargados del mes para evitar conflictos reales)
            const driverAllTrips = allMonthTrips.filter(t => t.driver_id === d.id && !['cancelled', 'cancelled_paid', 'no_show', 'anulado', 'draft'].includes(t.status?.toLowerCase()));
            
            const todayCount = driverAllTrips.filter(t => isSameDay(new Date(t.scheduled_time), today)).length;
            const selectedDurMins = Math.max(40, Number(selectedTrip?.estimated_duration_minutes) || 45);
            const selectedEnd = tripTime + (selectedDurMins + 20) * 60000;
            
            const conflict = driverAllTrips.some(t => {
                if (t.id === selectedTrip?.id) return false;
                if (t.status?.toLowerCase() === 'completed') return false;
                const tStart = new Date(t.scheduled_time).getTime();
                const tDurMins = Math.max(40, Number(t.estimated_duration_minutes) || 45);
                const tEnd = tStart + (tDurMins + 20) * 60000;
                return (tripTime < tEnd && selectedEnd > tStart);
            });

            if (conflict) {
                score -= 100;
                reasons.push({ text: "Conflicto Horario", color: "danger", icon: <Clock size={12}/> });
            } else {
                score += 50;
                reasons.push({ text: "Disponible", color: "success", icon: <CheckCircle size={12}/> });
            }

            // REGLA 1.5: PENALIZACIÓN POR RECHAZOS (RECHAZO DE ESTE SERVICIO Y RECHAZOS DEL MES)
            // A) Si el conductor ya rechazó este servicio específicamente
            const hasRejectedThisTrip = Array.isArray(selectedTrip?.rejected_driver_ids) && selectedTrip.rejected_driver_ids.includes(d.id);
            if (hasRejectedThisTrip) {
                score -= 1000; // Gran penalización para que no quede primero ni recomendado
                reasons.push({ text: "Rechazó este servicio", color: "danger", icon: <XCircle size={12}/> });
            }

            // B) Penalización por rechazos acumulados en el mes
            const monthRejections = d.monthly_rejections_count || 0;
            if (monthRejections > 0) {
                const penalty = monthRejections * 25;
                score -= penalty;
                reasons.push({ 
                    text: `${monthRejections} rechazo${monthRejections > 1 ? 's' : ''} mes (-${penalty} pts)`, 
                    color: "warning", 
                    icon: <AlertTriangle size={12}/> 
                });
            }

            // REGLA 2: Equidad de Ingresos MENSUALES en Moneda Local
            const monthTrips = driverAllTrips.filter(t => new Date(t.scheduled_time) >= monthStart);
            const monthEarnings = monthTrips.reduce((acc, t) => acc + (t.driver_earnings || 0), 0);
            
            // Detectar moneda del chofer (desde su base o último viaje)
            const driverCurrency = monthTrips.length > 0 ? monthTrips[0].local_currency_code : (d.base?.local_currency_code || 'CLP');
            
            if (monthEarnings === 0) score += 30;
            else if (monthEarnings < 500000) score += 15;

            reasons.push({ text: "Base OK", color: "primary", icon: <Car size={12}/> });

            // REGLA 3: ENCADENAMIENTO INTELIGENTE (TRIP CHAINING / RETORNOS)
            const selectedTripDate = new Date(selectedTrip.scheduled_time);
            
            // Encontrar viajes del mismo día del conductor
            const sameDayTrips = driverAllTrips.filter(t => 
                isSameDay(new Date(t.scheduled_time), selectedTripDate)
            );

            // Encontrar el viaje que termina más cercano antes del viaje seleccionado
            let priorTrip = null;
            let maxPriorEndTime = 0;

            sameDayTrips.forEach(t => {
                const tStart = new Date(t.scheduled_time).getTime();
                const duration = t.estimated_duration_minutes || 60;
                const tEnd = tStart + duration * 60000;
                
                if (tEnd <= tripTime && tEnd > maxPriorEndTime) {
                    maxPriorEndTime = tEnd;
                    priorTrip = t;
                }
            });

            if (priorTrip) {
                const marginMin = (tripTime - maxPriorEndTime) / 60000;
                // Margen óptimo de encadenado: entre 15 min y 2 horas
                if (marginMin >= 15 && marginMin <= 120) {
                    const isPriorDestAir = isAirport(priorTrip.dropoff_address);
                    const isNewOrigAir = isAirport(selectedTrip.pickup_address);
                    
                    if (isPriorDestAir && isNewOrigAir) {
                        score += 200; // Prioridad ultra alta
                        reasons.push({ 
                            text: `Retorno Aeropuerto (${Math.round(marginMin)} min)`, 
                            color: "success", 
                            icon: <Plane size={12}/> 
                        });
                    } else if (priorTrip.dropoff_address && selectedTrip.pickup_address) {
                        const priorDestClean = priorTrip.dropoff_address.toLowerCase().split(',')[0].trim();
                        const newOrigClean = selectedTrip.pickup_address.toLowerCase().split(',')[0].trim();
                        const isCerca = priorDestClean === newOrigClean || 
                                        priorDestClean.includes(newOrigClean) || 
                                        newOrigClean.includes(priorDestClean);
                        
                        if (isCerca) {
                            score += 150;
                            reasons.push({ 
                                text: `Termina en origen (${Math.round(marginMin)} min)`, 
                                color: "success", 
                                icon: <MapPin size={12}/> 
                            });
                        } else {
                            score += 70;
                            reasons.push({ 
                                text: `Termina antes (${Math.round(marginMin)} min)`, 
                                color: "info", 
                                icon: <Clock size={12}/> 
                            });
                        }
                    }
                }
            }

            return { ...d, vehicle, score, reasons, monthEarnings, driverCurrency, todayCount, conflict };
        }).filter(d => d !== null).sort((a, b) => b.score - a.score);
    }, [selectedTrip, drivers, vehicles, vehicleTypes, driverSearchTerm, allMonthTrips]);

    const handleAssign = async (driverId, forceOverride = false) => {
        if (!selectedTripId) return;
        setAssigning(true);
        try {
            const payload = { driver_id: driverId, status: 'assigned' };
            if (forceOverride) {
                payload.force_driver_assignment = true;
                payload.audit_override = true;
            }
            await updateTrip(selectedTripId, payload);
            toast.success("Conductor asignado exitosamente");
            setSelectedTripId(null);
            setDriverSearchTerm('');
            onRefresh();
        } catch (e) {
            const errorDetail = e.response?.data?.detail;
            if (errorDetail && typeof errorDetail === 'string' && errorDetail.startsWith('MARGIN_WARNING')) {
                const [, provName, cost, sale, sugProv] = errorDetail.split('|');
                const formatNum = (val) => Number(val || 0).toLocaleString('es-CL');
                const msg = `⚠️ Pérdida Detectada:\nEl costo de ${provName} ($${formatNum(cost)}) supera el valor de venta ($${formatNum(sale)}).\n\nProveedor recomendado con tarifa óptima: ${sugProv || 'Ninguno'}\n\n¿Deseas asignar a este conductor de todas formas?`;
                if (window.confirm(msg)) {
                    handleAssign(driverId, true);
                    return;
                }
            } else if (errorDetail && typeof errorDetail === 'string' && errorDetail.includes('Requiere confirmación para asignación forzada')) {
                if (window.confirm(`⚠️ Advertencia de Disponibilidad:\n${errorDetail}\n\n¿Deseas confirmar la asignación forzada de todas formas?`)) {
                    handleAssign(driverId, true);
                    return;
                }
            } else {
                const msg = errorDetail || "Error al asignar conductor";
                toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div className="mt-3">
            <Row>
                <Col md={4}>
                    <Card className="shadow-sm border-0" style={{ height: 'calc(100vh - 250px)' }}>
                        <Card.Header className="bg-primary text-white py-2">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0 small fw-bold">VIAJES SIN ASIGNAR ({pendingTrips.length})</h5>
                                <Calendar size={16}/>
                            </div>
                            <Form.Select 
                                size="sm" 
                                className="bg-white text-dark border-0 shadow-sm fw-bold"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                            >
                                <option value="today">Solo Hoy</option>
                                <option value="tomorrow">Solo Mañana</option>
                                <option value="upcoming">Próximos 7 días</option>
                                <option value="all">Ver Todos (Futuros)</option>
                            </Form.Select>
                        </Card.Header>
                        <ListGroup variant="flush" className="overflow-auto">
                            {pendingTrips.map(t => {
                                const isActive = selectedTripId === t.id;
                                const isAir = isAirport(t.pickup_address) || isAirport(t.dropoff_address);
                                return (
                                <ListGroup.Item 
                                    key={t.id} 
                                    action 
                                    active={isActive}
                                    onClick={() => { setSelectedTripId(t.id); setDriverSearchTerm(''); }}
                                    className="p-3 border-bottom"
                                >
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className={`fw-bold ${isActive ? 'text-white' : 'text-primary'}`}>
                                            #{t.id} - {t.passenger_name}
                                        </div>
                                        <div className="text-end">
                                            <Badge bg={isActive ? "light" : "white"} text="dark" className="border d-block mb-1">
                                                <Clock size={10} className="me-1"/>
                                                {format(new Date(t.scheduled_time), 'HH:mm')}
                                            </Badge>
                                            <small className={`${isActive ? 'text-white' : 'text-muted'} fw-bold`} style={{fontSize:'10px'}}>
                                                {format(new Date(t.scheduled_time), 'dd MMM', { locale: es })}
                                            </small>
                                        </div>
                                    </div>
                                    
                                    <div className={`mt-2 small ${isActive ? 'text-white opacity-90' : 'text-dark'}`}>
                                        <div className="text-truncate mb-1">
                                            <MapPin size={12} className={`me-1 ${isActive ? 'text-white' : 'text-success'}`}/> 
                                            {isAirport(t.pickup_address) && <Plane size={12} className="me-1 text-info"/>}
                                            <strong>Orig:</strong> {t.pickup_address}
                                        </div>
                                        <div className="text-truncate opacity-75">
                                            <MapPin size={12} className={`me-1 ${isActive ? 'text-white' : 'text-danger'}`}/> 
                                            {isAirport(t.dropoff_address) && <Plane size={12} className="me-1 text-info"/>}
                                            <strong>Dest:</strong> {t.dropoff_address}
                                        </div>
                                    </div>

                                    <div className="mt-2 d-flex gap-2">
                                        <Badge bg={isActive ? "white" : "info"} text={isActive ? "primary" : "white"} className="fw-normal">
                                            <Car size={10} className="me-1"/>{t.requested_vehicle_type?.name || 'Standard'}
                                        </Badge>
                                        {t.client?.name && (
                                            <Badge bg={isActive ? "white" : "secondary"} text={isActive ? "dark" : "white"} className="fw-normal opacity-75">
                                                {t.client.name}
                                            </Badge>
                                        )}
                                        {isAir && <Badge bg="warning" text="dark"><Plane size={10}/></Badge>}
                                    </div>
                                </ListGroup.Item>
                            )})}
                            {pendingTrips.length === 0 && (
                                <div className="p-4 text-center text-muted small">No hay viajes pendientes en este rango.</div>
                            )}
                        </ListGroup>
                    </Card>
                </Col>

                <Col md={8}>
                    <Card className="shadow-sm border-0" style={{ height: 'calc(100vh - 250px)' }}>
                        <Card.Header className="bg-white py-2 border-bottom">
                            <Row className="align-items-center">
                                <Col md={6}>
                                    <h5 className="mb-0 small fw-bold text-dark">
                                        CONDUCTORES SUGERIDOS {selectedTrip && <span className="text-primary">- Viaje #{selectedTrip.id}</span>}
                                    </h5>
                                </Col>
                                <Col md={6}>
                                    {selectedTrip && (
                                        <InputGroup size="sm">
                                            <InputGroup.Text className="bg-light border-end-0"><Search size={14} className="text-muted"/></InputGroup.Text>
                                            <Form.Control 
                                                placeholder="Buscar por nombre o móvil..." 
                                                className="bg-light border-start-0"
                                                value={driverSearchTerm}
                                                onChange={(e) => setDriverSearchTerm(e.target.value)}
                                            />
                                        </InputGroup>
                                    )}
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="overflow-auto p-0">
                            {!selectedTrip ? (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-50">
                                    <ChevronRight size={48} />
                                    <p>Selecciona un viaje de la izquierda para ver candidatos</p>
                                </div>
                            ) : isMonthLoading ? (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                                    <Spinner animation="border" variant="primary" className="mb-2" />
                                    <p className="small">Calculando disponibilidad y retornos...</p>
                                </div>
                            ) : (
                                <ListGroup variant="flush">
                                    {driverRanking.map((d, idx) => (
                                        <ListGroup.Item key={d.id} className="p-3">
                                            <Row className="align-items-center">
                                                <Col md={1} className="text-center">
                                                    <div className={`rounded-circle d-flex align-items-center justify-content-center ${idx === 0 && !driverSearchTerm ? 'bg-success text-white' : 'bg-light text-muted'}`} style={{width: 35, height: 35, fontSize: '0.9rem', fontWeight: 'bold'}}>
                                                        {idx + 1}
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="fw-bold fs-6">{d.full_name}</div>
                                                    <div className="small text-muted d-flex align-items-center gap-2">
                                                        <Badge bg="dark"><Car size={10} className="me-1"/>Móvil: {d.vehicle?.internal_code || '---'}</Badge>
                                                        <Badge bg="primary" variant="outline"><Calendar size={10} className="me-1"/>Hoy: {d.todayCount}</Badge>
                                                    </div>
                                                </Col>
                                                <Col md={4}>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {d.reasons.map((r, i) => (
                                                            <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{r.text}</Tooltip>}>
                                                                <Badge bg={r.color} className="fw-normal d-flex align-items-center gap-1" style={{fontSize: '0.65rem'}}>
                                                                    {r.icon} {r.text}
                                                                </Badge>
                                                            </OverlayTrigger>
                                                        ))}
                                                    </div>
                                                    <div className="mt-1">
                                                        <small className="text-muted"><TrendingUp size={10} className="me-1"/>Ganado en el mes: <strong>${d.monthEarnings.toLocaleString()} {d.driverCurrency}</strong></small>
                                                    </div>
                                                </Col>
                                                <Col md={3} className="text-end">
                                                    <Button 
                                                        variant={d.conflict ? "outline-warning" : "outline-success"} 
                                                        size="sm" 
                                                        className="fw-bold px-3"
                                                        disabled={assigning}
                                                        onClick={() => {
                                                            if (d.conflict) {
                                                                if (window.confirm(`⚠️ Conflicto Horario:\nEl conductor ${d.full_name} ya tiene un viaje asignado en un horario similar.\n\n¿Deseas asignarlo de todas formas?`)) {
                                                                    handleAssign(d.id, true);
                                                                }
                                                            } else {
                                                                handleAssign(d.id, false);
                                                            }
                                                        }}
                                                    >
                                                        {d.conflict ? "Asignar con Riesgo" : "Asignar Ahora"}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </ListGroup.Item>
                                    ))}
                                    {driverRanking.length === 0 && (
                                        <div className="p-5 text-center text-danger">
                                            <AlertCircle size={32} className="mb-2"/><br/>
                                            {driverSearchTerm ? "No se encontraron conductores." : "No hay conductores disponibles autorizados para esta base/vehículo."}
                                        </div>
                                    )}
                                </ListGroup>
                            )}
                        </Card.Body>
                        {selectedTrip && (
                            <Card.Footer className="bg-light p-2 text-center small text-muted">
                                <Info size={12} className="me-1"/> El ranking prioriza disponibilidad, base y equidad de ingresos mensuales en moneda local.
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SmartAssignmentView;
